// @ts-strict-ignore
import * as React from 'react';

import Checkbox from 'antd/lib/checkbox';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { BetaTestingGroup, IntegrationType, RunwayResponseType } from '../../types';
import Form from 'antd/lib/form';
import { AddBetaGroupsToBuildRequest, tryUpdateRelease } from '../../actions/release';
import { useDispatch, useSelector } from 'react-redux';
import { ReleaseUpdatable } from '../../actions/updatable';
import Link from 'antd/lib/typography/Link';
import { RootState } from '../../reducers/reducers';
import { selectBetaIntegrationLinkUrl, selectStepRun } from '../../selectors/selectors';
import { PLAY_CONSOLE_URL } from '../../constants';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface BetaGroupsDialogProps extends ReleaseComponentProps {
    open: boolean;
    onCancel: () => void;
    betaGroups: {
        selected: string[];
        all: BetaTestingGroup[];
    };
    onSaveSuccess: () => void;
    externalBuildId: string;
    betaIntegrationType: IntegrationType;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const BetaTestersDialog = (props: BetaGroupsDialogProps): JSX.Element => {
    const testerGroups = props.betaGroups.all?.map((betaGroup) => ({
        label: betaGroup.name,
        value: betaGroup.id,
        disabled: props.betaGroups.selected.includes(betaGroup.id),
    }));

    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [
        assignBetaGroupsToBuild,
        assignBetaGroupsToBuildIsLoading,
        assignBetaGroupsToBuildResponseType,
        assignBetaGroupsToBuildError,
    ] = useCallUpdatable(StepRunUpdatable.AssignBetaGroupsToBuild, props.stepRunComponentProps);

    const isAddLoadingReleaseStep = useIsLoading(ReleaseUpdatable.AddBetaGroupsToBuild, props.releaseId);
    const isAddSuccessReleaseStep = useUpdated(ReleaseUpdatable.AddBetaGroupsToBuild, props.releaseId);
    const addErrorReleaseStep = useError(ReleaseUpdatable.AddBetaGroupsToBuild, props.releaseId);

    const isAddLoading = props.stepRunComponentProps ? assignBetaGroupsToBuildIsLoading : isAddLoadingReleaseStep;
    const isAddSuccess =
        props.stepRunComponentProps ?
            assignBetaGroupsToBuildResponseType === RunwayResponseType.Success
        :   isAddSuccessReleaseStep;
    const addError = props.stepRunComponentProps ? assignBetaGroupsToBuildError : addErrorReleaseStep;

    const integrationLinkUrl = useSelector((state: RootState) => {
        if (props.betaIntegrationType === IntegrationType.GoogleBeta && props.stepRunComponentProps?.stepRunId) {
            return (
                selectStepRun(state, props.stepRunComponentProps.stepRunId)?.data?.summaryData?.consoleUrl ??
                PLAY_CONSOLE_URL
            );
        }
        return selectBetaIntegrationLinkUrl(state, props.appId, props.betaIntegrationType);
    });

    const onSubmit = (values) => {
        const groupsIdsToAdd = props.betaGroups.all
            .filter((group) => !props.betaGroups.selected.includes(group.id) && values.groupIds.includes(group.id))
            .map((group) => group.id);
        const request: AddBetaGroupsToBuildRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            buildId: props.externalBuildId,
            groupIds: groupsIdsToAdd,
        };

        if (props.stepRunComponentProps) {
            assignBetaGroupsToBuild(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.AddBetaGroupsToBuild, request, props.releaseId));
        }
    };

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue({ groupIds: props.betaGroups.selected });
        }
    }, [props.open]);

    React.useEffect(() => {
        if (isAddSuccess) {
            globalMessage.success('Added beta group to build!');

            props.onSaveSuccess();
        }
    }, [isAddSuccess]);

    React.useEffect(() => {
        if (addError) {
            globalMessage.error(addError);
        }
    }, [addError]);

    const onCancel = () => {
        props.onCancel();
    };

    const additionalInfo = () => {
        switch (props.betaIntegrationType) {
            case IntegrationType.GoogleBeta:
                return (
                    <Text type="secondary">
                        Go to the{' '}
                        <Link href={integrationLinkUrl} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                            Play Console
                        </Link>{' '}
                        to select other lists, or to view and edit individual testers.
                    </Text>
                );
            case IntegrationType.FirebaseBeta:
                return (
                    <Text type="secondary">
                        Go to{' '}
                        <Link href={integrationLinkUrl} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                            Firebase App Distribution
                        </Link>{' '}
                        to create new Groups or edit existing Groups.
                    </Text>
                );
            case IntegrationType.TestFlight:
                return (
                    <>
                        <Text type="secondary">
                            Go to{' '}
                            <Link href={integrationLinkUrl} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                                App Store Connect
                            </Link>{' '}
                            to create new Groups, edit existing Groups, or remove Groups that currently have access.
                        </Text>
                        <Text type="secondary">
                            All eligible builds are automatically available to users in the App Store Connect Users
                            group.
                        </Text>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            open={props.open}
            title={betaGroupsDialogTitle(props.betaIntegrationType)}
            onCancel={onCancel}
            onOk={() => form.submit()}
            okText={'Save'}
            okButtonProps={{ loading: isAddLoading }}
            focusTriggerAfterClose={false}
            className="modal-with-background"
        >
            <div>
                <Space direction="vertical" size="large">
                    {additionalInfo()}
                    <Form onFinish={onSubmit} form={form} layout="vertical" colon={false}>
                        <Form.Item name="groupIds" label={<Text strong>Groups</Text>}>
                            <Checkbox.Group options={testerGroups} className="checkbox-group-vertical" />
                        </Form.Item>
                    </Form>
                </Space>
            </div>
        </Modal>
    );
};

export default BetaTestersDialog;

const betaGroupsDialogTitle: (integrationId: IntegrationType) => string = (integrationId) => {
    switch (integrationId) {
        case IntegrationType.GoogleBeta:
            return 'Google Groups';
        case IntegrationType.TestFlight:
        case IntegrationType.FirebaseBeta:
            return 'Edit testing groups';
        default:
            return null;
    }
};
